import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/Modal/Modal";
import { departementsEtRegions } from "./donnees";
import { updatePartenaire } from "../../../services/partenaireskataServices";
import { FaSave, FaTimes, FaMapMarkerAlt, FaUser, FaLock, FaEnvelope } from "react-icons/fa";

const GRADES = [
  "1er Dan",
  "2ème Dan",
  "3ème Dan",
  "4ème Dan",
  "5ème Dan",
  "6ème Dan",
  "7ème Dan",
  "8ème Dan",
  "9ème Dan",
  "10ème Dan",
];

function FieldLabel({ children }) {
  return (
    <label className="mb-1 block text-sm font-semibold text-slate-800 dark:text-slate-100">
      {children}
    </label>
  );
}

export default function EditPartenaireModal({
  partenaire,
  isOpen,
  onClose,
  refreshList,
}) {
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    region: "",
    departement: "",
    club: "",
    passage_grade: "",
    content: "",
    tel: "",
    mail: "",
    password: "",
  });

  useEffect(() => {
    if (partenaire) {
      setFormData({
        nom: partenaire.nom ?? "",
        prenom: partenaire.prenom ?? "",
        region: partenaire.region ?? "",
        departement:
          partenaire.departement !== undefined && partenaire.departement !== null
            ? String(partenaire.departement)
            : "",
        club: partenaire.club ?? "",
        passage_grade: partenaire.passage_grade ?? "",
        content: partenaire.content ?? "",
        tel: partenaire.tel ?? "",
        mail: partenaire.mail ?? "",
        password: "",
      });
    }
  }, [partenaire]);

  const regions = useMemo(
    () => [...new Set(departementsEtRegions.map((i) => i.region))],
    []
  );

  const filteredDepartements = useMemo(
    () => departementsEtRegions.filter((dep) => dep.region === formData.region),
    [formData.region]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "region") {
      setFormData((prev) => ({ ...prev, region: value, departement: "" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      const payload = {
        nom: formData.nom,
        prenom: formData.prenom,
        region: formData.region,
        departement:
          formData.departement === "" ? null : parseInt(formData.departement, 10),
        club: formData.club,
        passage_grade: formData.passage_grade,
        content: formData.content,
        tel: formData.tel,
        mail: formData.mail,
      };

      if (formData.password && formData.password.trim() !== "") {
        payload.password = formData.password;
      }

      await updatePartenaire(partenaire.id, payload);
      await refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de la mise à jour du partenaire :", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      labelledById="edit-kata-title"
      describedById="edit-kata-desc"
      initialFocusSelector='input[name="nom"]'
    >
      <ModalHeader
        id="edit-kata-title"
        title="Éditer l’annonce partenaire Kata"
        subtitle="Mettez à jour vos informations, disponibilités et coordonnées"
        onClose={onClose}
      />

      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <ModalBody id="edit-kata-desc">
          <div className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <p className="text-sm italic text-slate-600 dark:text-slate-300">
                RGPD : vos coordonnées restent visibles tant que votre annonce est publiée.
              </p>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                <FaUser className="text-slate-500 dark:text-slate-400" />
                Identité
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <FieldLabel>Nom</FieldLabel>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <FieldLabel>Prénom</FieldLabel>
                  <input
                    type="text"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                <FaMapMarkerAlt className="text-slate-500 dark:text-slate-400" />
                Localisation et objectif
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <FieldLabel>Région</FieldLabel>
                  <select
                    name="region"
                    value={formData.region}
                    onChange={handleChange}
                    className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    required
                  >
                    <option value="">Sélectionnez une région</option>
                    {regions.map((region, i) => (
                      <option key={i} value={region}>
                        {region}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <FieldLabel>Département</FieldLabel>
                  <select
                    name="departement"
                    value={formData.departement}
                    onChange={handleChange}
                    className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    required
                    disabled={!formData.region}
                  >
                    <option value="">Sélectionnez un département</option>
                    {filteredDepartements.map((dep) => (
                      <option key={dep.code} value={dep.code}>
                        {dep.departement}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <FieldLabel>Club</FieldLabel>
                  <input
                    type="text"
                    name="club"
                    value={formData.club}
                    onChange={handleChange}
                    className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <FieldLabel>Passage de grade</FieldLabel>
                  <select
                    name="passage_grade"
                    value={formData.passage_grade}
                    onChange={handleChange}
                    className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    required
                  >
                    <option value="">Sélectionnez votre futur grade</option>
                    {GRADES.map((g, i) => (
                      <option key={i} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <FieldLabel>Description</FieldLabel>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  rows={5}
                  required
                />
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                <FaEnvelope className="text-slate-500 dark:text-slate-400" />
                Contact et sécurité
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <FieldLabel>Téléphone</FieldLabel>
                  <input
                    type="tel"
                    name="tel"
                    value={formData.tel}
                    onChange={handleChange}
                    className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <FieldLabel>Email</FieldLabel>
                  <input
                    type="email"
                    name="mail"
                    value={formData.mail}
                    onChange={handleChange}
                    className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    required
                  />
                </div>
              </div>

              <div className="mt-4">
                <FieldLabel>Mot de passe (laisser vide si inchangé)</FieldLabel>
                <div className="relative">
                  <FaLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="h-11 w-full rounded-2xl border border-slate-300 bg-white pl-11 pr-4 text-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    placeholder="Nouveau mot de passe"
                  />
                </div>
              </div>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex h-11 items-center gap-2 rounded-2xl bg-indigo-600 px-5 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Mise à jour..." : "Mettre à jour"}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
}